import React, { useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { usePresenceState } from '../../context/PresenceContext';
import { profilePictureUrl, memberInitials } from '../../utils/profilePicture';

const displayName = (member) => (
  `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.username || member.email
);

// Entry ids go into a CSS attribute selector; anything else is dropped.
const SAFE_ID = /^[A-Za-z0-9_-]+$/;
// Keep only letters/digits in the ::after text, so nothing can break out of the CSS string.
const cssText = (value) => String(value || '').replace(/[^\p{L}\p{N}]/gu, '');
// A URL inside url("..."): drop anything that could end the string or the function.
const cssUrl = (value) => String(value || '').replace(/["'\\()\s<>]/g, '');

/**
 * Entry id -> the other people (on this page) whose edit form is open on it.
 * Only `editing` counts on the calendar -- it has no selection to mirror.
 */
export const calendarEntryEditors = (peers, grid) => {
  const byId = new Map();
  peers.forEach(({ member, focus }) => {
    if (!focus || focus.grid !== grid || !focus.editing || !SAFE_ID.test(focus.editing)) return;
    const list = byId.get(focus.editing) || [];
    if (!list.some((m) => m.email === member.email)) list.push(member);
    byId.set(focus.editing, list);
  });
  return byId;
};

/**
 * One rule pair per edited entry: a thin brand-pink outline on the event and
 * the first editor's avatar (picture, or initials) in its top-right corner.
 */
export const buildCalendarPresenceCss = (editors, theme) => {
  const pink = theme.palette.secondary.main;
  const ring = theme.palette.background.paper;
  const fallbackBg = theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400];
  const rules = [];
  editors.forEach((members, id) => {
    const first = members[0];
    const picture = cssUrl(profilePictureUrl(first.email, first.profile_picture_version));
    const selector = `.fc .fc-event[data-entry-id="${id}"]`;
    rules.push(
      `${selector}{position:relative;outline:2px solid ${pink};outline-offset:-2px;}`,
      `${selector}::after{content:"${picture ? '' : cssText(memberInitials(first.first_name, first.last_name))}";`
        + 'position:absolute;top:2px;right:2px;width:14px;height:14px;box-sizing:border-box;border-radius:50%;'
        + `border:1px solid ${ring};background:${fallbackBg}${picture ? ` url("${picture}") center/cover no-repeat` : ''};`
        + 'color:#fff;font-size:7px;font-weight:700;line-height:12px;text-align:center;z-index:4;pointer-events:none;}',
    );
  });
  return rules.join('\n');
};

/**
 * Presence markers on FullCalendar events without touching FullCalendar:
 * CalendarComponent's `eventDidMount` stamps each event element with
 * `data-entry-id`, and this renders a <style> keyed on those ids. The events
 * array (which must stay referentially stable, or the whole week re-renders)
 * never changes for presence. Hover titles are set on the matching elements
 * here and, for events mounted later, from `labelsRef` in eventDidMount.
 */
const CalendarPresenceMarkers = ({ grid = 'time_entries', labelsRef }) => {
  const theme = useTheme();
  const { peers } = usePresenceState();
  const editors = useMemo(() => calendarEntryEditors(peers, grid), [peers, grid]);
  const css = useMemo(() => buildCalendarPresenceCss(editors, theme), [editors, theme]);

  useEffect(() => {
    const labels = {};
    editors.forEach((members, id) => {
      labels[id] = members.map((m) => `${displayName(m)} is editing this entry`).join(' · ');
    });
    if (labelsRef) labelsRef.current = labels;
    document.querySelectorAll('.fc-event[data-presence-title]').forEach((el) => {
      if (!labels[el.dataset.entryId]) {
        el.removeAttribute('title');
        el.removeAttribute('data-presence-title');
      }
    });
    Object.entries(labels).forEach(([id, title]) => {
      document.querySelectorAll(`.fc-event[data-entry-id="${id}"]`).forEach((el) => {
        el.setAttribute('title', title);
        el.setAttribute('data-presence-title', '1');
      });
    });
  }, [editors, labelsRef]);

  return css ? <style data-presence="calendar">{css}</style> : null;
};

export default CalendarPresenceMarkers;
